import { useCallback, useContext, useMemo } from "react";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "../order/relayerResponse";
import { ethers } from "ethers";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { AuthContext } from "../../../contexts/AuthContext";
import { getCurrentChainConfig } from "../../../config/chains";
import { authApi } from "../../../services/api/apiFetcher";
import { formatContractError } from "../../../utils/errors";
import { getAgentWallet } from "../../../utils/oneClick";
import { getGaslessPerpRouter, getPerpDomainName } from "../../../utils/gaslessDomains";

const PERP_LEVERAGE_ABI = [
  "function setAccountLeverage(address market, uint16 leverage) external",
];

export const useAccountLeverage = () => {
  const { account, masterAccount, apiKey, apiSecret } = useContext(AuthContext);
  const fetcher = useMemo(
    () => authApi(apiKey, apiSecret),
    [apiKey, apiSecret]
  );

  const updateAccountLeverage = useCallback(
    async (instrumentId: string, leverage: number) => {
      if (!account) {
        throw new Error("Wallet not connected");
      }
      const normalizedLeverage = Math.max(1, Math.round(Number(leverage)));
      if (!Number.isFinite(normalizedLeverage) || normalizedLeverage > 65535) {
        throw new Error("Invalid leverage");
      }

      const config = getCurrentChainConfig();
      const perpEngine =
        config.contracts.perpetualDex?.perpRouter ||
        config.contracts.perpetualDex?.orderHandler;
      const gaslessPerpRouter = getGaslessPerpRouter(config);
      const perpDomainName = getPerpDomainName(config);
      if (!perpEngine) {
        throw new Error("Perp contract not configured");
      }

      try {
        const provider = await getWeb3ProviderForChain(config);
        const signer = provider.getSigner();

        // ── Gasless leverage update: sign EIP-712 SetLeverage + relay (user pays NO gas) ──
        // One-click signs with the agent key (no popup); otherwise the connected wallet signs once.
        if (config.gasless && config.relayerUrl && gaslessPerpRouter) {
          const relayer = config.relayerUrl.replace(/\/+$/, "");
          const nonceRes = await fetch(`${relayer}/nonce/${account}?router=leverage`);
          const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
          const deadline = String(Math.floor(Date.now() / 1000) + 3600);
          const domain = {
            name: perpDomainName,
            version: "1",
            chainId: config.chainId,
            verifyingContract: gaslessPerpRouter,
          };
          const types = {
            SetLeverage: [
              { name: "account", type: "address" },
              { name: "market", type: "address" },
              { name: "leverage", type: "uint16" },
              { name: "nonce", type: "uint256" },
              { name: "deadline", type: "uint256" },
            ],
          };
          const message = {
            account,
            market: instrumentId,
            leverage: normalizedLeverage,
            nonce,
            deadline,
          };
          const agentWallet = getAgentWallet(masterAccount || account, config.chainId);
          const signature = agentWallet
            ? await agentWallet._signTypedData(domain, types, message)
            : await signer._signTypedData(domain, types, message);
          const relayRes = await fetch(`${relayer}/relay`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              action: "setLeverage",
              params: {
                account,
                market: instrumentId,
                leverage: normalizedLeverage,
                nonce,
                deadline,
              },
              signature,
            }),
          });
          const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
          if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
            throw new Error(relayJson?.error || "Gasless leverage update failed. Please try again.");
          }
        } else {
          const contract = new ethers.Contract(
            perpEngine,
            PERP_LEVERAGE_ABI,
            signer
          );
          const args = [instrumentId, normalizedLeverage] as const;
          const estimatedGas = await contract.estimateGas.setAccountLeverage(
            ...args
          );
          const tx = await contract.setAccountLeverage(...args, {
            gasLimit: estimatedGas.mul(120).div(100),
          });
          await tx.wait(1);
        }
      } catch (error: any) {
        throw new Error(
          formatContractError(error) ||
            error?.message ||
            "Failed to update leverage on-chain"
        );
      }

      const { data } = await (
        await fetcher.postAccountLeverage({
          instrument_id: instrumentId,
          leverage: normalizedLeverage,
        } as any)
      )();

      return data;
    },
    [account, fetcher]
  );

  return {
    updateAccountLeverage,
  };
};
